import * as React from "react";
import {
    Box,
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
// アコーディオンの展開/折りたたみアイコンのために追加
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LOCAL_CONSTANT } from "../Const";
import {
    tableStyle,
    tableHeaderStyle,
    tableCellStyle,
    tableCellStyleWhiteSpace,
    tableHeaderSmallStyle,
    tableCellSmallStyle,
    tableHeaderDateStyle,
    tableCellDateStyle,
    tableCellWhiteSpace,
    tableHeaderNameStyle,
    tableCellNameStyle,
    commentHeaderStyle,
    commentCellStyle,
} from "../components/ChangeHistoryTableStyle";
import { apiRequest } from "../../api/ApiCall";
import { useUtilityContext } from "../../utility-provider";
import ChangeHistoryTableComment from "../components/ChangeHistoryTableComment";
import FilterModal from "../components/FilterModal";
import { FilterCriteria, initFilterCriteriaObj, FilterOptions, initFilterOptionsObj, ChangeHistoryItem, initChangeHistoryList } from "../../data/type";
import dayjs from "dayjs";
import {
    changeHistoryTableName,
    changeHistoryColumnName,
    changeHistorySelecterDisplay,
    addSortColumnToData,
    generateFilterOptions,
    applyFilter,
    dateSort,
    pageSort,
} from "../components/ChangeHistoryFunc";
import { useUserContext } from "../../user-provider";
import { decryptQueryParam } from '../../utility/util';
import { usePlanAreaSearchByIdForHeader } from "../Common";

export interface ItemChangeHistoryProps {
    props: {
        width: number;
        height: number;
    };
}

// アコーディオン本体のスタイル - 幅を1230pxに固定
const accordionStyles = {
    marginTop: "10px",     // 上部マージンでRFC表との間隔を確保
    marginLeft: "10px",    // 左側マージンで画面端からの余白を確保
    marginRight: "10px",   // 右側マージンで画面端からの余白を確保
    width: "1230px",       // アコーディオンの幅を1230pxに固定
    border: "1px solid #ccc",  // 枠線を表示してテーブルと統一感を確保
    // Material-UIのデフォルトスタイルを無効化して枠線のずれを防ぐ
    boxShadow: "none",     // デフォルトの影を無効化
    "&:before": {          // デフォルトの疑似要素境界線を無効化
        display: "none"
    },
    "&.Mui-expanded": {    // 展開時も同じスタイルを維持
        margin: "10px 10px 10px 10px",  // 展開時のマージンを明示的に指定
        width: "1230px",    // 展開時も幅を1230pxに固定
    }
};

// アコーディオンヘッダーのスタイル - レイアウトとアイコン位置制御
const accordionSummaryStyles = {
    minHeight: "48px",     // ヘッダーの最小高さを固定（RFC表のヘッダーと統一）
    display: "flex",       // フレックスボックスレイアウトを適用
    alignItems: "center",  // 垂直方向中央揃えでタイトルとボタンを整列
    // コンテンツエリアのスタイル（タイトルとボタンを含む領域）
    "& .MuiAccordionSummary-content": {
        display: "flex",        // フレックスボックスでタイトルとボタンを横並び配置
        alignItems: "center",   // タイトルとボタンの垂直方向中央揃え
        width: "100%",          // 利用可能な幅を最大限活用
        margin: 0,              // Material-UIデフォルトマージンをリセット
    },
    // 展開アイコン（ExpandMoreIcon）の位置制御 - 右端に配置
    "& .MuiAccordionSummary-expandIconWrapper": {
        marginLeft: "auto",     // 右端に配置（縦位置は変更せず）
        display: "flex",        // フレックスボックスでアイコンを中央揃え
        alignItems: "center",   // 垂直方向中央揃え
    }
};

const ItemChangeHistory: React.FC<ItemChangeHistoryProps> = ({ props }) => {
    const utilityCtx = useUtilityContext();
    //URLパラメータ
    const [planAreaId, setPlanAreaId] = React.useState("");
    const [branch, setBranch] = React.useState("");
    const [changeHistory, setChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList);
    const [comment, setComment] = React.useState<any[]>([]);
    const [tableNameMaster, setTableNameMaster] = React.useState<any>([]);
    const [commentOpen, setCommentOpen] = React.useState<boolean>(false);
    const [codeMaster, setCodeMaster] = React.useState<any>();
    const [userType, setUserType] = React.useState<string>("");
    // RR項目変更履歴アコーディオンの展開/折りたたみ状態を管理
    // デフォルトはtrue（展開状態）
    const [rrAccordionExpanded, setRrAccordionExpanded] = React.useState<boolean>(true);
    
    // フィルタ関連の状態管理
    const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false); // フィルタモーダルの開閉状態
    const [originalChangeHistory, setOriginalChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList); // フィルタ前の元データ
    const [filterCriteria, setFilterCriteria] = React.useState<FilterCriteria>(initFilterCriteriaObj);  // 現在のフィルタ条件
    const [filterOptions, setFilterOptions] = React.useState<FilterOptions>(initFilterOptionsObj); // フィルタ選択肢
    
    const user = useUserContext();
    const updateHeader = usePlanAreaSearchByIdForHeader();


    React.useEffect(() => {
        //暗号化されたクエリパラメータを復号化
        const search = window.location.search;
        const urlParam = search.substring(1);
        decryptQueryParam(urlParam).then((res) => {
            setPlanAreaId(res[0]);
            setBranch(res[1]);
        }).catch((e:any) => {
            console.log(e);
            if (utilityCtx.hideSpinner) {
                utilityCtx.hideSpinner();
            }
        });
        const tableNameMasterObj = localStorage.getItem("tableNameMaster");
        if (tableNameMasterObj) {
            setTableNameMaster(JSON.parse(tableNameMasterObj));
        }
        const res: any = localStorage.getItem("codeMaster");
        if (res) {
            const d = JSON.parse(res);
            setCodeMaster(d);
        }
    }, []);

    React.useEffect(()=>{
        if(planAreaId !== ""){
            callChangeHistory();
            // 候補地情報をRedux storeに設定（共有関数を使用）
            if (user.userData) {
                updateHeader(planAreaId, user.userData);
            }
        }
    },[planAreaId, user.userData]);

    const callChangeHistory = () => {
        const params = {
            mode: "regist",
            endPoint: "/location/system/v1/get-log",
            query: {
                plan_area_id: planAreaId,
                log_type: "rfc",
            },
        };
        const commentParams = {
            mode: "regist",
            endPoint: "/location/system/v1/get-rr-comment",
            query: {
                plan_area_id: planAreaId,
            },
        };
        if (utilityCtx.showSpinner) {
            utilityCtx.showSpinner();
        }
        (async () => {
            try {
                const res = await apiRequest(params);
                // データにsort_columnを追加する処理 ページソートで使用するため
                const enhancedData = addSortColumnToData(res.data, codeMaster, tableNameMaster);
                setOriginalChangeHistory(enhancedData);
                setChangeHistory(enhancedData);
                const filterOpts = generateFilterOptions(enhancedData, codeMaster);
                setFilterOptions(filterOpts);
                const comment_res = await apiRequest(commentParams);
                setComment(comment_res.data);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            } catch (e) {
                console.log(e);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            }
        })();
    };

    const change_details = (log_type: string) => {
        const change_details_list = [
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ITEM_CHANGE, // "0"
            "",
            "",
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_CVS_CHANGE, // "3"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_SMDS_CHANGE, // "4"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_INTERSECTION_CHANGE, // "5"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.SALES_FORECAST_SUMMARY_TABLE_CHANGE, // "6"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ADDITIONAL_SURVEY_CHANGE, // "7"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.OTHER_DOCUMENT_CHANGE, // "8"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.RR_INTERIOR_CHANGE, // "9"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CURRENT_STATUS_PHOTO_CHANGE, // "10"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRAFFIC_VOLUME_CHANGE, // "11"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.FIVEM_EXTRA_CALCULATION_BASIS_CHANGE, // "12"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRADE_AREA_CUT_CHANGE, // 13
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_ASSESSMENT_CHANGE, // 14
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.MAP_CHANGE, // 15
        ];

        return change_details_list[Number(log_type)];
    };

    const handleCommentOpen = () => {
        setCommentOpen(true);
    };

    const handleSaveComment = () => {
        setCommentOpen(false);
        callChangeHistory();
    };

    const handleCanCelComment = () => {
        setCommentOpen(false);
    };

    const displayTime = (date: string) => {
        const time = new Date(date);
        time.setHours(time.getHours() + 9);
        return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
    };

    // RFC項目変更履歴の高さを動的に計算
    // RRアコーディオンの状態に応じてRFC表の高さを調整し、画面スペースを有効活用
    const getRfcTableHeight = () => {
        if (rrAccordionExpanded) {
            return "40%"; // RRアコーディオンが展開されている場合は従来通り40%
        } else {
            return "75%"; // RRアコーディオンが折りたたまれている場合は高さを75%に拡張
        }
    };

    // アコーディオンの展開/折りたたみハンドラー
    // アコーディオンの状態変更時にRFC表の高さも動的に調整される
    const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
        setRrAccordionExpanded(isExpanded);
    };

    // フィルタを適用
    const handleApplyFilter = (filters: FilterCriteria) => {
        const filteredData = applyFilter(originalChangeHistory, filters, tableNameMaster);
        setChangeHistory(filteredData);
        setFilterCriteria(filters);
    };

    // フィルタモーダルを開く
    const handleFilterOpen = () => {
        setFilterModalOpen(true);
    };

    // フィルタモーダルを閉じる
    const handleFilterClose = () => {
        setFilterModalOpen(false);
    };

    // ページソート
    const handlePageSort = () => {
        const sortedData = pageSort(changeHistory);
        setChangeHistory(sortedData);
    };

    // 日付ソート
    const handleDateSort = () => {
        const sortedData = dateSort(changeHistory);
        setChangeHistory(sortedData);
    };

    //ユーザー取得
    React.useEffect(() => {
        if (JSON.parse(user.userData || "null") !== null) {
            setUserType(JSON.parse(user.userData || "null").user_type);
        }
    }, [user]);

    return (
        <Box sx={{ width: "100%" }}>
            <Grid container>
                <Grid item xs={12} sx={{ height: "48px" }} />
            </Grid>
            <Grid container direction={"row"} sx={{ position: "fixed" }}>
                <Grid item sx={{ width: "55px", height: props.height }} />
                <Grid
                    item
                    sx={{ width: props.width, height: props.height, overflowY: "scroll" }}>
                    <Grid item sx={{ marginTop: "10px", marginLeft: "10px", marginBottom: "10px" }}>
                        <Box sx={{ 
                            display: "flex", 
                            alignItems: "center",
                            minHeight: "48px",
                            paddingY: "8px"
                        }}>
                            <Typography sx={{ display: "flex", alignItems: "center" }}>
                                {LOCAL_CONSTANT.LABEL.RFC_ITEM_CHANGE_HISTORY}
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleFilterOpen}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.FILTER}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handlePageSort}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.PAGE_SORT}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleDateSort}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.DATE_SORT}
                            </Button>
                        </Box>
                    </Grid>
                    {/* RFC項目変更履歴テーブル - 動的高さ調整を適用 */}
                    <TableContainer sx={{ height: getRfcTableHeight(), minHeight: getRfcTableHeight(), maxHeight: getRfcTableHeight(), overflowY: "auto" }}>
                        <Table stickyHeader sx={tableStyle}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderDateStyle}>{LOCAL_CONSTANT.LABEL.DATE}</TableCell>
                                    <TableCell sx={tableHeaderNameStyle}>{LOCAL_CONSTANT.LABEL.EDITOR}</TableCell>
                                    <TableCell sx={tableHeaderSmallStyle}>{LOCAL_CONSTANT.LABEL.BRANCH}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.PAGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.ITEM}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_BEFORE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.AFTER_CHANGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_DETAILS}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {changeHistory.map(
                                    (row: any, index: number) => (
                                        <TableRow key={ row.primaryKey + "-time-" + row.publish_time + "-" + index }>
                                            <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                            <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                            <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                            <TableCell sx={tableCellWhiteSpace}>{changeHistoryTableName(tableNameMaster, row.target_table, row)}</TableCell>
                                            <TableCell sx={tableCellWhiteSpace}>{changeHistoryColumnName(tableNameMaster, row.target_column, row)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{changeHistorySelecterDisplay(codeMaster, row.target_column, row.target_before_value)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{changeHistorySelecterDisplay(codeMaster, row.target_column, row.target_after_value)}</TableCell>
                                            <TableCell sx={tableCellStyle}>{change_details(row.log_type)}</TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* RR項目変更履歴をアコーディオン化 - 展開/折りたたみ可能 */}
                    <Accordion 
                        expanded={rrAccordionExpanded} 
                        onChange={handleAccordionChange}
                        sx={accordionStyles}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="rr-accordion-content"
                            id="rr-accordion-header"
                            sx={accordionSummaryStyles}
                        >
                            <Typography sx={{ display: "flex", alignItems: "center" }}>
                                {LOCAL_CONSTANT.LABEL.RR_ITEM_CHANGE_HISTORY}
                            </Typography>
                            {/* リサーチのときのみコメント登録ボタンを表示 */}
                            {userType === LOCAL_CONSTANT.USER_TYPE.RR && (
                                <Button
                                    onClick={(e) => {
                                        e.stopPropagation(); // アコーディオンの展開/折りたたみを防ぐ
                                        handleCommentOpen();
                                    }}
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    sx={{ 
                                        margin: "0px 20px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {LOCAL_CONSTANT.LABEL.REGIST_COMMENT}
                                </Button>
                            )}
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TableContainer sx={{ height: "300px", overflowY: "auto" }}>
                                <Table stickyHeader sx={tableStyle}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={tableHeaderDateStyle}>{LOCAL_CONSTANT.LABEL.DATE}</TableCell>
                                            <TableCell sx={tableHeaderNameStyle}>{LOCAL_CONSTANT.LABEL.EDITOR}</TableCell>
                                            <TableCell sx={commentHeaderStyle}>{LOCAL_CONSTANT.LABEL.COMMENT}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {comment.map((row: any) => (
                                            <TableRow key={row.publish_time}>
                                                <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                <TableCell sx={commentCellStyle}>{row.comment}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
            <ChangeHistoryTableComment
                props={{
                    open: commentOpen,
                    params: {
                        planAreaId: planAreaId,
                        branch: branch,
                        comment: "",
                    },
                    handleModal: setCommentOpen,
                    handleSaveComment: handleSaveComment,
                    handleCanCelComment: handleCanCelComment,
                }}
            />
            <FilterModal
                open={filterModalOpen}
                onClose={handleFilterClose}
                onApplyFilter={handleApplyFilter}
                filterOptions={filterOptions}
                currentFilters={filterCriteria}
            />
        </Box>
    );
};

export default ItemChangeHistory;
